import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Search, Users } from 'lucide-react';
import { Button, DataTablePagination, PageTitle } from '@/components';
import { Loader } from '@/components/ui';
import { ConfirmActionModal } from '@/components/ui/ConfirmActionModal';
import { cn } from '@/lib/utils';
import { useGetPatients, useRemovePatient } from '@/services';
import { encodeUrl } from '@/utils';
import { CreatePatientModal } from '../../sessions/components/CreatePatientModal';
import { ProfessionalProntuarioModal } from '../../home/components/ProfessionalProntuarioModal';
import { ProfessionalPatientRow } from './ProfessionalPatientRow';
import { ProfessionalSubpageHeader } from '../../components/ProfessionalSubpageHeader';

type ProfessionalPatientsManagerProps = {
	professionalId?: string;
	professional?: Professionals.Professional | null;
	pageTitle?: string;
	pageDescription?: string;
	listTitle?: string;
	listTitleClassName?: string;
	subpageHeaderTitle?: string;
	subpageHeaderBreadcrumb?: [string, string];
	showScheduleAction?: boolean;
	showProntuarioAction?: boolean;
	enabled?: boolean;
	onBack?: () => void;
};

const ProfessionalPatientsManager = ({
	professionalId,
	professional = null,
	pageTitle,
	pageDescription,
	listTitle = 'Pacientes',
	listTitleClassName,
	subpageHeaderTitle,
	subpageHeaderBreadcrumb,
	showScheduleAction = false,
	showProntuarioAction = false,
	enabled = true,
	onBack,
}: ProfessionalPatientsManagerProps) => {
	const navigate = useNavigate();

	const [page, setPage] = useState(1);
	const [limit] = useState(10);
	const [searchTerm, setSearchTerm] = useState('');
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [patientToEdit, setPatientToEdit] = useState<Patients.Patient | null>(
		null,
	);
	const [patientToDelete, setPatientToDelete] =
		useState<Patients.Patient | null>(null);
	const [prontuarioPatientId, setProntuarioPatientId] = useState<
		string | null
	>(null);

	useEffect(() => {
		setPage(1);
	}, [searchTerm, limit]);

	const queryParams = useMemo(
		() => ({
			page,
			limit,
			sort: 'full_name' as const,
			sort_direction: 'asc' as const,
			...(searchTerm.trim() ? { search: searchTerm.trim() } : {}),
			...(professionalId ? { professional_id: professionalId } : {}),
		}),
		[page, limit, searchTerm, professionalId],
	);

	const { data, isFetching, isLoading } = useGetPatients(queryParams, {
		enabled,
	});

	const patients = data?.data ?? [];
	const totalPatients = data?.pagination?.total ?? 0;

	const { mutate: removePatient, isPending: isRemovingPatient } =
		useRemovePatient(() => {
			setPatientToDelete(null);
		});

	const buildAgendarUrl = (patientId: string) => {
		if (!professional?.id) return null;

		const serviceId =
			professional.professional_services?.[0]?.service?.id ?? '';

		return encodeUrl({
			s: '1',
			patient_id: patientId,
			professional_id: professional.id,
			service_id: serviceId,
			user_ids: '[]',
		});
	};

	const handleSchedulePatient = (patient: Patients.Patient) => {
		const encodedParams = buildAgendarUrl(patient.id);
		if (!encodedParams) return;
		navigate(`/profissional/consultas/agendar?d=${encodedParams}`);
	};

	const handleOpenProntuario = (patient: Patients.Patient) => {
		setProntuarioPatientId(patient.id);
	};

	const handleConfirmDelete = () => {
		if (!patientToDelete) return;
		removePatient(patientToDelete.id);
	};

	if (!enabled) {
		return null;
	}

	const showPageHeader = Boolean(onBack || pageTitle || pageDescription);
	const showSubpageHeader = Boolean(onBack && subpageHeaderTitle);

	const patientsCountLabel = `${totalPatients} cadastrado${
		totalPatients !== 1 ? 's' : ''
	}`;

	const newPatientButton = (
		<Button
			text="Novo paciente"
			className="w-full shrink-0 rounded-full bg-black px-6 py-2.5 font-bold shadow-md hover:bg-neutral-800 sm:w-auto"
			startIcon={<Plus className="size-5 stroke-[3] text-white" />}
			onClick={() => setIsCreateModalOpen(true)}
		/>
	);

	return (
		<div className="flex min-h-full w-full justify-center px-4 py-4 md:px-6 md:py-5">
			<section className="w-full max-w-[1340px] animate-fade-in space-y-5">
				{showSubpageHeader ? (
					<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
						<ProfessionalSubpageHeader
							title={subpageHeaderTitle!}
							breadcrumb={subpageHeaderBreadcrumb}
							subtitle={patientsCountLabel}
							onBack={onBack!}
						/>
						{newPatientButton}
					</div>
				) : null}

				{showPageHeader && !showSubpageHeader ? (
					<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
						<div className="flex min-w-0 items-start gap-4">
							{onBack && (
								<button
									type="button"
									onClick={onBack}
									className="group mt-1 flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-stone-100 bg-white transition-all hover:border-stone-200 hover:shadow-sm"
									aria-label="Voltar"
								>
									<ArrowLeft
										size={20}
										className="text-stone-400 group-hover:text-stone-900"
									/>
								</button>
							)}
							<div className="min-w-0 space-y-1">
								{pageTitle ? (
									<PageTitle text={pageTitle} />
								) : null}
								{pageDescription ? (
									<p className="text-sm text-stone-400">
										{pageDescription}
									</p>
								) : null}
								<h3
									className={cn(
										'text-lg font-black text-stone-900',
										listTitleClassName,
									)}
								>
									{listTitle}
								</h3>
								<p className="text-sm text-stone-400">
									{patientsCountLabel}
								</p>
							</div>
						</div>
						{newPatientButton}
					</div>
				) : null}

				<div className="relative w-full">
					<Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-stone-400" />
					<input
						type="search"
						value={searchTerm}
						onChange={(event) => setSearchTerm(event.target.value)}
						placeholder="Buscar por nome, CPF, telefone ou RG"
						className="w-full rounded-xl border border-stone-200 bg-white py-2.5 pl-9 pr-3 text-sm text-stone-800 outline-none focus:border-brand-purple"
					/>
				</div>

				<div className="space-y-2">
					{isLoading || isFetching ? (
						<div className="flex items-center justify-center py-16">
							<Loader />
						</div>
					) : patients.length === 0 ? (
						<div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-stone-200 bg-white px-6 py-16 text-center">
							<div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-stone-100 text-stone-500">
								<Users className="size-7" />
							</div>
							<h4 className="text-base font-bold text-stone-900">
								Nenhum paciente encontrado
							</h4>
							<p className="mt-2 max-w-sm text-sm text-stone-500">
								{searchTerm.trim()
									? 'Tente outro termo de busca ou cadastre um novo paciente.'
									: 'Cadastre o primeiro paciente deste profissional.'}
							</p>
							<Button
								text="Novo paciente"
								className="mt-6 rounded-full bg-black px-6 py-2.5 font-bold shadow-md hover:bg-neutral-800"
								startIcon={
									<Plus className="size-5 stroke-[3] text-white" />
								}
								onClick={() => setIsCreateModalOpen(true)}
							/>
						</div>
					) : (
						patients.map((patient) => (
							<ProfessionalPatientRow
								key={patient.id}
								patient={patient}
								onEdit={setPatientToEdit}
								onDelete={setPatientToDelete}
								onSchedule={
									showScheduleAction
										? handleSchedulePatient
										: undefined
								}
								onOpenProntuario={
									showProntuarioAction
										? handleOpenProntuario
										: undefined
								}
							/>
						))
					)}
				</div>

				{totalPatients > limit && (
					<DataTablePagination
						page={page}
						pageSize={limit}
						totalItems={totalPatients}
						onPageChange={setPage}
					/>
				)}

				<CreatePatientModal
					isOpen={isCreateModalOpen}
					professionalId={professionalId}
					canEditWhatsAppNotifications
					onClose={() => setIsCreateModalOpen(false)}
					onSuccess={() => setIsCreateModalOpen(false)}
				/>

				<CreatePatientModal
					isOpen={!!patientToEdit}
					patient={patientToEdit}
					professionalId={professionalId}
					canEditWhatsAppNotifications
					onClose={() => setPatientToEdit(null)}
					onSuccess={() => setPatientToEdit(null)}
				/>

				<ConfirmActionModal
					isOpen={!!patientToDelete}
					onClose={() => setPatientToDelete(null)}
					onConfirm={handleConfirmDelete}
					title="Excluir paciente?"
					description={
						patientToDelete?.full_name
							? `Tem certeza que deseja excluir ${patientToDelete.full_name}? Esta ação não pode ser desfeita.`
							: 'Tem certeza que deseja excluir este paciente? Esta ação não pode ser desfeita.'
					}
					confirmButtonText={
						isRemovingPatient ? 'Excluindo...' : 'Excluir paciente'
					}
					disableConfirm={isRemovingPatient}
				/>

				{prontuarioPatientId ? (
					<ProfessionalProntuarioModal
						isOpen={!!prontuarioPatientId}
						onClose={() => setProntuarioPatientId(null)}
						patientId={prontuarioPatientId}
						initialView="sessions-list"
					/>
				) : null}
			</section>
		</div>
	);
};

export { ProfessionalPatientsManager };
